import type { ObjectId } from '@freightview/object-id'

import type { EquipmentType } from '../equipment/constants.ts'
import {
  vArray,
  vCurrency,
  vMinValue,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
} from '../validation/index.ts'
import { type ChargeCode, type ChargeName, chargeNames } from './charge.ts'
import type {
  Currency,
  Mode,
  PaymentTerms,
  PricingMethod,
  PricingType,
  QuoteMethodType,
  QuoteSourceType,
  QuoteStatus,
} from './common.ts'
import type { EngineErrorType } from './engine-error.ts'

export interface BaseQuoteModel {
  _id: ObjectId
  accountNumber?: string
  amount: number
  amountUsd?: number
  assetCarrierCode?: string
  assetCarrierName?: string
  carrierId: string
  charges?: ApiChargeDTO[]
  createdDate: Date
  currency: Currency
  deliveryDate?: Date
  endOfDay?: boolean
  equipmentType: EquipmentType
  errors?: ApiQuoteError[]
  expirationDate?: Date | null
  guaranteed?: boolean
  interline?: boolean
  isSpot?: boolean
  isSaltRate?: boolean
  loadQuoteVersion?: number
  method: QuoteMethodType
  mode: Mode
  notes?: string[]
  paymentTerms?: PaymentTerms
  pickupDate?: Date
  pricingDescription?: string
  pricingMethod: PricingMethod
  pricingType: PricingType
  providerCode?: string
  providerName: string
  quotedBy?: ApiQuotedByDTO
  quoteNum?: string | null
  quoteRequestId: string
  refNums?: Array<{ type: string; value: string }>
  scrape?: boolean
  serviceDescription?: string
  serviceId: string
  source: QuoteSourceType
  status: QuoteStatus
  terminals?: ApiQuoteTerminal[]
  transitDaysMax?: number
  transitDaysMin?: number
  time?: number
  filterName?: string
}

export interface QuoteDTO extends BaseQuoteModel {
  hasMarkup?: boolean
}

export interface SelectedQuoteProps {
  modifiedAfterBooking?: boolean
  amountAtBooking?: number
}

export interface SelectedQuote extends QuoteDTO, SelectedQuoteProps {}

export interface ApiQuoteError {
  type: EngineErrorType
  message: string
}
export interface ApiChargeDTO {
  name: ChargeName
  amount: number
  code?: ChargeCode
  currency: Currency
  providerDetail?: string
}
export interface ApiQuotedByDTO {
  userId: string
  name: string
}

export interface ApiQuoteTerminal {
  terminalId: string
  terminalType: TerminalType
}
export type TerminalType = 'origin terminal' | 'destination terminal'

export const updateChargeSchema = vObject({
  name: vPicklist(chargeNames),
  amount: vPipe(vNumber()),
  providerDetail: vOptional(vString()),
  currency: vCurrency('usd'),
})
export type UpsertChargeDTO = vOutput<typeof updateChargeSchema>

export const updateQuoteSchema = vObject({
  quoteId: vObjectId(),
  amount: vPipe(vNumber(), vMinValue(0)),
  currency: vCurrency('usd'),
  charges: vOptional(vArray(updateChargeSchema)),
})

export type UpdateQuoteDTO = vOutput<typeof updateQuoteSchema>

export type QuoteHistory = {
  date: Date
  amount: number
  charges?: ApiChargeDTO[]
  userId?: string
  method?: QuoteMethodType
  quoteNum?: string
}

export type AdvancedBidResult = {
  isFinalBid: boolean
}

export function displayPricingType(pricingType?: PricingType): string {
  switch (pricingType) {
    case 'pallet':
      return 'Pallet Pricing'
    case 'density':
      return 'Density Pricing'
    case 'spot':
      return 'Spot Pricing'
  }

  return ''
}
